// file: src/pages/pricing/PricingPage.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Check, Gift, Clock } from 'lucide-react';
import styles from './PricingPage.module.css';
import TaskBar from '../../components/taskbar/TaskBar';
import Footer from '../../components/footer/Footer';
import billingServices from '../../services/billingServices';

/**
 * PricingPage component - Public pricing page for unauthenticated users
 * Based on home page aesthetic with billing card content
 */
export default function PricingPage() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setLoading(true);
        const fetchedPlans = await billingServices.getPublicPlans();
        
        // If API fails or returns empty, use fallback data
        if (!Array.isArray(fetchedPlans) || fetchedPlans.length === 0) {
          console.log('Using fallback pricing data');
          const fallbackPlans = [
    {
      id: 'pro',
      name: 'Pro',
              price: 50.00,
      interval: 'month',
      features: ['Unlimited projects', 'Metered 2D/3D CAD servers', 'Metered Agent requests', 'Advanced usage analytics']
    },
    {
      id: 'business',
      name: 'Business',
              price: 200.00,
      interval: 'month',
      features: ['Everything in Pro', 'Extended limits on 2D/3D CAD servers', 'Extended limits on Agent requests', 'Advanced project analytics', 'Multi-user access', 'Productivity tools integrations']
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      price: 'Custom',
      interval: '',
      features: ['Everything in Business', 'Custom integrations', 'Dedicated support', 'SLA guarantees', 'On-premise deployment', 'Custom training']
    }
  ];
          setPlans(fallbackPlans);
        } else {
          setPlans(fetchedPlans);
        }
      } catch (err) {
        console.error('Failed to fetch plans:', err);
        // Set fallback data on error
        setPlans([
          {
            id: 'pro',
            name: 'Pro',
            price: 50.00,
            interval: 'month',
            features: ['Unlimited projects', 'Metered 2D/3D CAD servers', 'Metered Agent requests', 'Advanced usage analytics']
          },
          {
            id: 'business',
            name: 'Business',
            price: 200.00,
            interval: 'month',
            features: ['Everything in Pro', 'Extended limits on 2D/3D CAD servers', 'Extended limits on Agent requests', 'Advanced project analytics', 'Multi-user access', 'Productivity tools integrations']
          },
          {
            id: 'enterprise',
            name: 'Enterprise',
            price: 'Custom',
            interval: '',
            features: ['Everything in Business', 'Custom integrations', 'Dedicated support', 'SLA guarantees', 'On-premise deployment', 'Custom training']
          }
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, []);

  return (
    <>
      <TaskBar />
      <div className={styles.pricingPage}>

      {/* Pricing Section */}
      <section className={styles.pricingSection}>
        <div className={styles.pricingContainer}>
          <h1 className={styles.heroTitle}>
            Simple, transparent pricing
          </h1>
          
          {/* Divider text */}
          <p className={styles.plansDivider}>Try Foundry risk-free</p>

          {/* Free Trial Card */}
          <div className={styles.trialCard}>
            <div className={styles.trialBadge}>
              <Gift className={styles.trialBadgeIcon} />
              <span>Early Adopter Offer</span>
            </div>
            <div className={styles.trialFeatures}>
              <div className={styles.trialFeature}>
                <Clock className={styles.trialFeatureIcon} />
                <div>
                  <strong>7-day free trial</strong>
                  <p>Full access to all features, cancel anytime</p>
                </div>
              </div>
              <div className={styles.trialFeature}>
                <Gift className={styles.trialFeatureIcon} />
                <div>
                  <strong>$25 credit for early adopters</strong>
                  <p>Applied during your free trial to explore premium features</p>
                </div>
              </div>
            </div>
            <Link to="/login" className={styles.trialButton}>
              Start your free trial
            </Link>
          </div>

          {/* Divider text */}
          <p className={styles.plansDivider}>or choose a plan that's right for you</p>

          {/* Pricing Cards */}
          {loading ? (
            <div className={styles.loadingMessage}>Loading plans...</div>
          ) : (
            <div className={styles.pricingGrid}>
              {plans.map((plan) => (
                <div key={plan.id} className={styles.pricingCard}>
                  <div className={styles.planHeader}>
                    <h3 className={styles.planName}>{plan.name}</h3>
                    <div className={styles.planPrice}>
                      {typeof plan.price === 'number' ? (
                        <>
                          <span className={styles.priceAmount}>${plan.price}</span>
                          <span className={styles.priceInterval}>/user/month</span>
                        </>
                      ) : (
                        <span className={styles.priceAmount}>{plan.price}</span>
                      )}
                    </div>
                  </div>
                  <div className={styles.planFeatures}>
                    {plan.features.map((feature, index) => (
                      <div key={index} className={styles.feature}>
                        <Check className={styles.featureIcon} />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Start Building Button */}
          <div className={styles.startBuildingWrapper}>
            <Link to="/login" className={styles.primaryButton}>
              Start building
            </Link>
          </div>
        </div>
      </section>

        <Footer />
      </div>
    </>
  );
}
